import React, { useState, useEffect, useRef, useContext } from 'react'

import {
    useNavigate,
    useParams,
    useSearchParams,
    useLocation,
} from 'react-router-dom'
import {
    Contract,
    ContractStage,
    Attachment,
    CurrencyList,
    ContractCountAndSum,
    ContractSearch,
    ImportMessage,
} from './types'

import { useAuth } from '../../../hooks/auth'
import { Structure } from '../Org/Structure/types'
import { OrganizationInfo } from '../Org/Home/types'

import Checkbox from '../../../components/form/Checkbox'

// import './style.css'
// import '../../../components/Toolbar/Icon.css'

import ContractHeader from './components/Header'
import ContractStageTab from './components/Tabs/ContractStageTab'
import ContractsTableRow from './components/TableRow'
import LoadingIcon from '../../../components/LoadingIcon'
import TableStageToolbarButtons from './components/Buttons/TableStageToolbarButtons'
import TableStageToolbarDownButtons from './components/Buttons/TableStageToolbarDownButton'
import TableRightClickButton from './components/Buttons/TableRightClickButton'

import TableHeader from './components/TableHeader/TableHeader'

import {
    UserFilter,
    FilterSetting,
    Criteria,
} from './components/TableHeader/FilterTypes'

import FileUploaderBlock from './components/FileUploaderBlock'
import { Slide, ToastContainer } from 'react-toastify'

import { ThemeContext } from '../../../components/ThemeContext'

import HoverTooltip from '../../../components/HoverTooltip'

const REGISTRY_STAGE = 'Реестр договоров'

type RowStatus = 'idle' | 'pending' | 'success' | 'error' | 'error_back'

type ApiResponseRoleGuid = {
    result: QueryItem[]
}

type QueryItem = {
    guid: string
    name: string
    organization_guid: string
    created_at: string
    updated_at: string
}

const getCurrentDate = (): string => {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0') // добавляем 1, так как месяцы начинаются с 0
    const day = String(today.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

const getStructureList = async (): Promise<Structure[]> => {
    return fetch(
        process.env.REACT_APP_API_URL + '/api/backend/v1/organization_struct',
        {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        }
    )
        .then((response) => {
            if (response.ok) {
                return response.json()
            } else {
                throw new Error('Ошибка запроса на способы закупок')
            }
        })
        .catch((e) => {
            console.error(e)
        })
}

const getCurrencyList = async (): Promise<CurrencyList[]> => {
    return fetch(process.env.REACT_APP_API_URL + '/api/backend/v1/currency', {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    })
        .then((response) => {
            if (response.ok) {
                return response.json()
            } else {
                throw new Error('Ошибка запроса на курсы валют')
            }
        })
        .catch((e) => {
            console.error(e)
        })
}

const getRole = async (role_guid: string): Promise<QueryItem> => {
    try {
        const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/organizations/roles_by_guid/${role_guid}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )

        if (!response.ok) {
            throw new Error('Ошибка запроса на роль')
        }

        const data: ApiResponseRoleGuid = await response.json()
        // Извлекаем первый элемент массива, т.к. он всегда содержит единственный объект
        return data.result[0]
    } catch (error) {
        console.error('Ошибка:', error)
        // Возвращаем объект по умолчанию, соответствующий типу QueryItem
        return {
            guid: '',
            name: '',
            organization_guid: '',
            created_at: '',
            updated_at: '',
        }
    }
}

// Отдельная функция для проверки, есть ли скролл
const checkScroll = (element: HTMLElement | null): boolean => {
    ;``
    return element ? element.scrollHeight > element.clientHeight : false
}

const ContractListPage: React.FC = () => {
    const navigate = useNavigate()
    const location = useLocation()

    const { theme } = useContext(ThemeContext)

    const { user } = useAuth()

    const [orgData, setOrgData] = useState<OrganizationInfo>()

    const [filter, setFilter] = useState<UserFilter | null>(null)
    // Дополнительное состояние
    const [filterLoaded, setFilterLoaded] = useState(false)

    const [structureList, setStructureList] = useState<Structure[]>([])
    const [currencyList, setCurrencyList] = useState<CurrencyList[]>([])

    const [isUserRoleName, setUserRoleName] = useState('')

    const [searchParams] = useSearchParams()
    const initialStage = searchParams.get('stage') as ContractStage
    const initialYearPeriod = Number(searchParams.get('year'))

    const [stage, setStage] = useState<ContractStage>(
        initialStage || ContractStage.EDIT
    )

    const [yearPeriod, setYearPeriod] = useState<number | null>(
        initialYearPeriod || new Date().getFullYear()
    )

    const [editStage, setEditStage] = useState(null)
    const [readyStage, setReadyStage] = useState(null)
    const [sendStage, setSendStage] = useState(null)
    const [perfStage, setPerfStage] = useState(null)
    const [closeStage, setCloseStage] = useState(null)
    const [allStage, setAllStage] = useState(null)

    const [contractCountAndSum, setContractCountAndSum] =
        useState<ContractCountAndSum | null>(null)
    const [loadingCountSum, setLoadingCountSum] = useState(false)

    const [isLoading, setLoading] = useState<boolean>(true)
    const [isLoadingContracts, setLoadingContracts] = useState<boolean>(true)

    const [items, setItems] = useState<Contract[]>([])
    const [page, setPage] = useState(1) // -1 - no more data

    const [selectedItems, setSelectedItems] = useState<string[]>([])
    const [selectAll, setSelectAll] = useState<boolean>(false)

    const [massImportMode, setMassImportMode] = useState<boolean>(false)

    const [contractRegNumber, setContractRegNumber] = useState('')
    const [singleContractImportStatus, setSingleContractImportStatus] =
        useState<boolean | null>(false)
    const [isSingleContractImporting, setIsSingleContractImporting] =
        useState<boolean>(false)

    const [isSearchingContractFromEis, setSearchingContractFromEis] =
        useState<boolean>(false)
    const [serachContractFromEisData, setContractFromEisData] =
        useState<ContractSearch | null>(null)
    const [searchFromDate, setSearchFromDate] = useState('')
    const [searchToDate, setSearchToDate] = useState('')

    const [importFromEisPopup, setImportFromEisPopup] = useState<boolean>(false)
    const [isImportingFromEis, setImportingFromEis] = useState<boolean>(false)
    const [importMessage, setImportMessage] = useState<ImportMessage | null>(
        null
    )
    const [clickUpdateButton, setClickUpdateButton] = useState<boolean>(false)

    const [rowLoadStatus, setRowLoadStatus] = useState<
        Record<string, RowStatus>
    >({})
    const [rowLoadError, setRowLoadError] = useState<
        Record<string, string | undefined>
    >({})
    const abortImportRef = useRef<AbortController | null>(null)

    const importPopupAbortRef = useRef<AbortController | null>(null)

    const [deleteContractPopup, setDeleteContractPopup] =
        useState<boolean>(false)
    const [isDeleting, setDeleting] = useState<boolean>(false)
    const [deleteItems, setDeleteItems] = useState<Contract[]>([])

    const [menuVisible, setMenuVisible] = useState(false)
    const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 })

    const [uploadMessage, setUploadMessage] = useState<string | null>(null) // Сообщение о результате загрузки

    //  проверка скрол есть или нет
    const scrollRef = useRef<HTMLDivElement>(null)
    const [hasScroll, setHasScroll] = useState(false)

    const [isCreatingContract, setIsCreatingContract] = useState(false)

    const [updateItems, setUpdateItems] = useState<Contract[]>([])
    const [updateContractPopup, setUpdateContractPopup] = useState(false)

    const contractsAbortRef = useRef<AbortController | null>(null)
    const contractsRequestIdRef = useRef(0)

    const loadOrganizationData = async (): Promise<OrganizationInfo> => {
        return fetch(
            process.env.REACT_APP_API_URL + '/api/backend/v1/organization',
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Failed to fetch organization')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }

    // Функция для получения фильтров пользователя
    const getUserFilterTypeCategory = async (
        user_guid: string,
        filter_type: string,
        filter_category: string
    ): Promise<UserFilter> => {
        return fetch(
            process.env.REACT_APP_API_URL +
                `/api/backend/v1/user_and_filtertype?user_guid=${user_guid}&filter_type=${filter_type}&filter_category=${filter_category}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка запроса на фильтр пользователя')
                }
            })
            .then((data) => {
                // Если данные представляют собой массив, возвращаем первую позицию (либо null, если массив пуст)
                if (Array.isArray(data) && data.length > 0) {
                    return data[0]
                } else {
                    return null
                }
            })
            .catch((error) => {
                console.error(error)
                throw error
            })
    }

    // Функция для обновления фильтра пользователя по PUT-запросу
    const updateUserFilter = async (
        filterData: {
            filter_type: string
            filter_settings: FilterSetting
            filter_category: string
        },
        guid: string
    ): Promise<UserFilter> => {
        return fetch(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/user_filter/${guid}`,
            {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    accept: 'application/json',
                },
                body: JSON.stringify(filterData),
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error(
                        'Ошибка запроса на обновление фильтра пользователя'
                    )
                }
            })
            .catch((error) => {
                console.error(error)
                throw error
            })
    }

    const createUserFilter = async (filterData: {
        filter_type: string
        filter_settings: { criteria: Criteria | null; filter_enabled: boolean }
        filter_category: string
    }): Promise<UserFilter> => {
        return fetch(
            process.env.REACT_APP_API_URL + '/api/backend/v1/user_filter',
            {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    accept: 'application/json',
                },
                body: JSON.stringify(filterData),
            }
        )
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Ошибка создания фильтра пользователя')
                }
            })
            .catch((error) => {
                console.error(error)
                throw error
            })
    }

    const loadContracts = async (
        page_num = 1,
        userFilter: UserFilter | null = null
    ) => {
        // Отменяем предыдущий запрос договоров
        if (contractsAbortRef.current) {
            contractsAbortRef.current.abort()
        }

        const controller = new AbortController()
        contractsAbortRef.current = controller

        const requestId = ++contractsRequestIdRef.current

        const currentStage = stage
        const currentYearPeriod = yearPeriod

        setLoadingContracts(true)

        // Важно: при первой странице сразу переводим таблицу в состояние загрузки
        if (page_num === 1) {
            setPage(1)
            setItems([])
        }

        const pageSize = 100
        const postfix = 'contract_with_approval_and_user_reestr_data'

        const params = new URLSearchParams()

        if (typeof currentYearPeriod === 'number') {
            params.set('year', String(currentYearPeriod))
        }

        params.set('stage', currentStage)
        params.set('page', String(page_num))
        params.set('size', String(pageSize))

        if (userFilter?.filter_settings?.filter_enabled) {
            params.set(
                'filter_settings',
                JSON.stringify(userFilter.filter_settings)
            )
        }

        const url = `${process.env.REACT_APP_API_URL}/api/backend/v1/${postfix}?${params.toString()}`

        try {
            const response = await fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                signal: controller.signal,
            })

            if (!response.ok) {
                throw new Error(
                    'Failed to fetch contracts (page: ' + page_num + ')'
                )
            }

            const data: Contract[] = await response.json()

            // Если после этого запроса уже стартовал новый — ничего не обновляем
            if (requestId !== contractsRequestIdRef.current) {
                return
            }

            // Если запрос отменен — тоже ничего не обновляем
            if (controller.signal.aborted) {
                return
            }

            const shouldFilter = currentStage && currentStage !== REGISTRY_STAGE

            const filteredData = shouldFilter
                ? data.filter(
                      (item: Contract) => item.contract_stage === currentStage
                  )
                : data

            if (page_num === 1) {
                setItems(filteredData)
            } else {
                setItems((prev) => [...prev, ...filteredData])
            }

            setPage(filteredData.length < pageSize ? -1 : page_num + 1)
        } catch (e: any) {
            if (e?.name === 'AbortError') {
                return
            }

            console.error(e)

            if (requestId === contractsRequestIdRef.current) {
                if (page_num === 1) {
                    setItems([])
                }
                setPage(-1)
            }
        } finally {
            if (requestId === contractsRequestIdRef.current) {
                setLoadingContracts(false)
                contractsAbortRef.current = null
            }
        }
    }

    const loadContractCountStage = () => {
        setLoading(true)

        const url = new URL(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/contract_count_stage`
        )
        if (typeof yearPeriod === 'number')
            url.searchParams.set('year', String(yearPeriod))

        fetch(url.toString(), {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then((response) => {
                if (response.ok) {
                    return response.json()
                } else {
                    throw new Error('Failed to fetch count')
                }
            })
            .then((data) => {
                // Обновляем состояния
                setEditStage(data.edit_count || 0)
                setReadyStage(data.ready_count || 0)
                setSendStage(data.send_count || 0)
                setPerfStage(data.perf_count || 0)
                setCloseStage(data.close_count || 0)
                setAllStage(data.all_count || 0)
            })
            .catch((e) => {
                console.error(e)
                // Устанавливаем значения по умолчанию на случай ошибки
                setEditStage(null)
                setReadyStage(null)
                setSendStage(null)
                setPerfStage(null)
                setCloseStage(null)
                setAllStage(null)
            })
            .finally(() => {
                setLoading(false)
            })
    }

    const loadContractCountAndSumToHeader = async () => {
        setLoadingCountSum(true)

        const url = new URL(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/contracts_count_and_sum`
        )
        if (typeof yearPeriod === 'number')
            url.searchParams.set('year', String(yearPeriod))

        try {
            const response = await fetch(url.toString(), {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            })
            if (!response.ok) {
                setContractCountAndSum(null)
                throw new Error(
                    `Error: ${response.status} ${response.statusText}`
                )
            }
            const json: ContractCountAndSum = await response.json()
            setContractCountAndSum(json)
        } catch (e) {
            console.error('Error fetching count/sum:', e)
            setContractCountAndSum(null)
        } finally {
            setLoadingCountSum(false)
        }
    }

    const getContractByGuid = async (guid: string): Promise<Contract> => {
        return fetch(
            process.env.REACT_APP_API_URL + `/api/backend/v1/contract/${guid}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then(async (response) => {
                if (response.ok) {
                    return await response.json()
                } else {
                    throw new Error(
                        'Ошибка запроса по договору. Договор с таким GUID не найден'
                    )
                }
            })
            .catch((e) => {
                console.error('Ошибка:', e)
                return ''
            })
    }

    useEffect(() => {
        const fetchData = async () => {
            const organization = await loadOrganizationData()
            if (organization) {
                setOrgData(organization)
            }

            const role = await getRole(user?.object.role_id)
            setUserRoleName(role.name)

            const structs = await getStructureList()
            if (structs) {
                setStructureList(structs)
            }

            const currency = await getCurrencyList()
            if (currency) {
                setCurrencyList(currency)
            }
        }

        fetchData()
    }, [])

    useEffect(() => {
        return () => {
            if (contractsAbortRef.current) {
                contractsAbortRef.current.abort()
            }
        }
    }, [])

    // перенаправление при нажатии на tab-stage и обнудение select при изменении stage
    useEffect(() => {
        setSelectedItems([])
        setSelectAll(false)

        setFilterLoaded(false)

        getUserFilterTypeCategory(user?.object.user_id, stage, 'Договор')
            .then((data) => {
                setFilter(data)
            })
            .catch((error) => {
                console.error(error)
                setFilter(null)
            })
            .finally(() => {
                setFilterLoaded(true)
            })
    }, [stage])

    useEffect(() => {
        navigate(`/hub/contracts?year=${yearPeriod}&stage=${stage}`)
    }, [stage, yearPeriod])

    // Проверка скролла
    useEffect(() => {
        const updateScrollState = () => {
            setHasScroll(checkScroll(scrollRef.current))
        }

        // Первоначальная проверка
        updateScrollState()

        // Перепроверяем при изменении размеров окна
        window.addEventListener('resize', updateScrollState)
        return () => window.removeEventListener('resize', updateScrollState)
    }, [items])

    // Загрузка позиций при загрузке фильтра
    useEffect(() => {
        if (!filterLoaded) return // Ждём завершение загрузки фильтра

        setSelectedItems([])
        setSelectAll(false)

        loadContracts(1, filter)
    }, [filterLoaded, filter, yearPeriod, stage])

    useEffect(() => {
        loadContractCountStage()
        loadContractCountAndSumToHeader()
    }, [yearPeriod, stage])

    // При изменении yearPeriod
    // useEffect(() => {
    //     setSelectedItems([])
    //     loadContracts(1, filter)
    //     loadContractCountStage()
    //     loadContractCountAndSumToHeader()
    // }, [yearPeriod])

    // Функция для обработки изменения состояния отдельного checkbox
    const handleCheckboxChange = (pos_guid: string) => {
        setSelectedItems((prevSelectedItems) => {
            if (prevSelectedItems.includes(pos_guid)) {
                return prevSelectedItems.filter((itemId) => itemId !== pos_guid)
            } else {
                return [...prevSelectedItems, pos_guid]
            }
        })
    }

    // Функция для обработки изменения состояния отдельного checkbox при нажатии правой кнопкой
    const handleRightClickCheckboxChange = (pos_guid: string) => {
        setSelectedItems((prevSelectedItems) => {
            if (prevSelectedItems.includes(pos_guid)) {
                return prevSelectedItems // Если уже выбран, ничего не меняем
            }
            return [...prevSelectedItems, pos_guid] // Добавляем в выбранные
        })
    }

    // Функция для обработки изменения состояния "Select All"
    const handleSelectAllChange = () => {
        setSelectAll(!selectAll)
        if (!selectAll) {
            // Выбираем только те элементы, которые соответствуют текущей стадии
            const filteredItems =
                stage == 'Реестр договоров'
                    ? items.map((item) => item.contract_reg_number)
                    : items
                          .filter((item) => item.contract_stage === stage)
                          .map((item) => item.contract_reg_number)

            setSelectedItems(filteredItems)
        } else {
            // Сбрасываем выбор всех элементов
            setSelectedItems([])
        }
    }

    const initialValue = 0
    const sum = items.reduce(
        (accumulator, currentValue) => accumulator + Number(currentValue.price),
        initialValue
    )

    // Закрыть меню при клике вне его
    useEffect(() => {
        const handleOutsideClick = () => setMenuVisible(false)
        window.addEventListener('click', handleOutsideClick)

        return () => window.removeEventListener('click', handleOutsideClick)
    }, [])

    const handleContextMenu = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault() // Отключаем стандартное контекстное меню
        setMenuPosition({ x: e.pageX, y: e.pageY }) // Устанавливаем позицию меню
        setMenuVisible(true) // Показываем меню
    }

    // // Отдельный эффект на изменение filter:
    // useEffect(() => {
    //     if (filter && filter?.filter_settings?.filter_enabled) {
    //         loadContracts(1, filter)
    //     } else if (filter) {
    //         loadContracts() // Если фильтр есть, но он отключен
    //     }
    // }, [filter]) // <-- Запускается только после обновления filter

    const handleFilterChange = async (currentFilter?: UserFilter) => {
        const filterToUse = currentFilter || filter

        if (!filterToUse) return

        try {
            const updatedFilter = await updateUserFilter(
                {
                    filter_type: stage,
                    filter_settings: filterToUse.filter_settings,
                    filter_category: 'Договор',
                },
                filterToUse.guid
            )

            // setFilter(updatedFilter)
        } catch (error) {
            console.error('Ошибка обновления фильтра:', error)
        }
    }

    const handleFilterErase = async () => {
        if (filter) {
            try {
                const updatedFilter = await updateUserFilter(
                    {
                        filter_type: stage,
                        filter_settings: {
                            criteria: null,
                            filter_enabled: true,
                        },
                        filter_category: 'Договор',
                    },
                    filter.guid
                )
                setFilter(updatedFilter)
            } catch (error) {
                console.error('Ошибка обновления фильтра:', error)
            }
        }
    }

    // Функция, вызываемая по нажатию на кнопку
    const handleFilter = async () => {
        if (!filter) {
            try {
                const newFilter = await createUserFilter({
                    filter_type: stage,
                    filter_settings: {
                        criteria: null,
                        filter_enabled: true,
                    },
                    filter_category: 'Договор',
                })
                setFilter(newFilter)
            } catch (error) {
                console.error('Ошибка создания фильтра:', error)
            }
        } else {
            const currentSettings = filter.filter_settings || {
                filter_enabled: false,
                criteria: {} as Criteria,
            }

            const newFilter: UserFilter = {
                ...filter,
                filter_settings: {
                    ...currentSettings,
                    filter_enabled: !currentSettings.filter_enabled,
                },
            }

            try {
                const updatedFilter = await updateUserFilter(
                    {
                        filter_type: newFilter.filter_type,
                        filter_settings: newFilter.filter_settings,
                        filter_category: 'Договор',
                    },
                    newFilter.guid
                )
                setFilter(updatedFilter)
            } catch (error) {
                console.error('Ошибка обновления фильтра:', error)
            }
        }
    }

    const abortImportPopupRequests = () => {
        if (importPopupAbortRef.current) {
            importPopupAbortRef.current.abort()
            importPopupAbortRef.current = null
        }

        setImportingFromEis(false)
        setIsSingleContractImporting(false)
        setSearchingContractFromEis(false)
    }

    const handleImportFromEisPopup = () => {
        const isClosing = importFromEisPopup === true

        if (isClosing) {
            abortImportPopupRequests()

            loadContractCountStage()
            loadContractCountAndSumToHeader()
            loadContracts(1, filter)
        }

        setImportFromEisPopup(!importFromEisPopup)
        setContractFromEisData(null)
        setRowLoadStatus({})
        setRowLoadError({})
        setSingleContractImportStatus(null)
        setClickUpdateButton(false)
    }

    const searchContractFromEis = async (
        date_from: string,
        date_to: string,
        signal?: AbortSignal
    ) => {
        setClickUpdateButton(false)
        setSearchingContractFromEis(true)
        setContractFromEisData(null)

        const url = new URL(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/contracts_from_eis_by_inn`
        )
        url.searchParams.set('inn', String(orgData?.inn))
        url.searchParams.set('date_from', String(date_from))
        url.searchParams.set('date_to', String(date_to))

        try {
            const response = await fetch(url.toString(), {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                signal,
            })

            if (!response.ok) {
                setContractFromEisData(null)
                throw new Error(
                    `Error: ${response.status} ${response.statusText}`
                )
            }

            const json: ContractSearch = await response.json()
            setContractFromEisData(json)
        } catch (e: any) {
            if (e?.name === 'AbortError') {
                console.log('Поиск договоров из ЕИС отменен')
                return
            }

            console.error('Error fetching contracts from EIS:', e)
            setContractFromEisData(null)
        } finally {
            setSearchingContractFromEis(false)
        }
    }

    const importContractFromEis = async (
        contract_reg_number: string,
        signal?: AbortSignal
    ): Promise<{ ok: boolean; contract?: Contract; error?: string }> => {
        setIsSingleContractImporting(true)
        setClickUpdateButton(true)

        const url = new URL(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/contract_from_eis`
        )
        url.searchParams.set('contract_reg_number', String(contract_reg_number))

        try {
            const response = await fetch(url.toString(), {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                signal,
            })

            // Бэкенд может вернуть и 200, и не-200. Пытаемся прочитать JSON в любом случае.
            let payload: any = null
            try {
                payload = await response.json()
            } catch {
                // пустой ответ или не JSON
            }

            // Если пришло поле message — считаем это ошибкой (по ТЗ)
            if (payload?.message) {
                const detailMsg =
                    payload?.details || payload?.message || 'Ошибка импорта'
                setSingleContractImportStatus(false)
                setIsSingleContractImporting(false)
                return { ok: false, error: detailMsg }
            }

            // Если не ОК по HTTP, но и без message — тоже как ошибка
            if (!response.ok) {
                setSingleContractImportStatus(false)
                setIsSingleContractImporting(false)
                return {
                    ok: false,
                    error: `HTTP ${response.status} ${response.statusText}`,
                }
            }

            // Успех — вернули сам договор
            setSingleContractImportStatus(true)
            setIsSingleContractImporting(false)
            return { ok: true, contract: payload as Contract }
        } catch (e: any) {
            // Закрыть — это не ошибка бизнеса, вернем понятное сообщение
            if (e?.name === 'AbortError') {
                setSingleContractImportStatus(null)
                setIsSingleContractImporting(false)
                return { ok: false, error: 'Операция отменена' }
            }
            setSingleContractImportStatus(false)
            setIsSingleContractImporting(false)
            return { ok: false, error: String(e) }
        }
    }

    const importAllFoundContracts = async () => {
        setClickUpdateButton(true)
        if (!serachContractFromEisData?.data?.length) return

        setImportingFromEis(true)

        const controller = new AbortController()
        importPopupAbortRef.current = controller

        try {
            for (const row of serachContractFromEisData.data) {
                if (controller.signal.aborted) break

                const key = String(row.contract_reg_number)

                setRowLoadStatus((prev) => ({ ...prev, [key]: 'pending' }))
                setRowLoadError((prev) => ({ ...prev, [key]: undefined }))

                const res = await importContractFromEis(key, controller.signal)

                if (controller.signal.aborted) break

                if (res.ok) {
                    setRowLoadStatus((prev) => ({
                        ...prev,
                        [key]: 'success',
                    }))
                } else {
                    setRowLoadStatus((prev) => ({ ...prev, [key]: 'error' }))
                    setRowLoadError((prev) => ({ ...prev, [key]: res.error }))
                }
            }
        } finally {
            setImportingFromEis(false)
            importPopupAbortRef.current = null

            if (!controller.signal.aborted) {
                loadContracts(1, filter)
                loadContractCountStage()
            }
        }
    }

    const deleteContract = async (
        contract_reg_number: string
    ): Promise<void> => {
        const url =
            process.env.REACT_APP_API_URL +
            `/api/backend/v1/contract/contract_reg_number/${contract_reg_number}`

        try {
            const response = await fetch(url, {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            })

            if (!response.ok) {
                throw new Error(
                    `Failed to delete contract. Status: ${response.status}`
                )
            }

            console.log('Position deleted successfully')
        } catch (error) {
            console.error('Error while deleting contract:', error)
            setDeleting(false)
        } finally {
        }
    }

    const handleDeleteContractPopup = () => {
        if (deleteContractPopup == false) {
            const filteredData = items.filter((item) =>
                selectedItems.includes(item.contract_reg_number)
            )
            setDeleteItems(filteredData)
        }
        setDeleteContractPopup(!deleteContractPopup)
    }

    const handleDeleteClick = async () => {
        setDeleting(true)

        let remainingDeleteItems = [...selectedItems] // Копируем массив, чтобы избежать изменения оригинала.

        for (const item of selectedItems) {
            await deleteContract(item) // Отправляем запрос на удаление.
            // Уменьшаем массив, убирая удалённый элемент.
            remainingDeleteItems = remainingDeleteItems.filter(
                (i) => i !== item
            )
            const filteredDeleteItems = deleteItems.filter((delItem) =>
                remainingDeleteItems.includes(delItem.contract_reg_number)
            )
            setDeleteItems(filteredDeleteItems)
        }
        setSelectedItems([])
        setSelectAll(false)
        setDeleting(false)
        setDeleteContractPopup(false)
        loadContracts(1, filter)
        loadContractCountAndSumToHeader()
        loadContractCountStage()
    }

    const handleDeleteItem = (itemGuid: string) => {
        setDeleteItems((prevItems) =>
            prevItems.filter((item) => item.contract_reg_number !== itemGuid)
        )
        setSelectedItems((prevSelectedItems) =>
            prevSelectedItems.filter(
                (selectedGuid) => selectedGuid !== itemGuid
            )
        )
    }

    const createNewDraftEpContract = async (
        year: number
    ): Promise<Contract> => {
        const url = new URL(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/contract_new_draft_ep`
        )
        url.searchParams.set('year', String(year))

        const response = await fetch(url.toString(), {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        })

        if (!response.ok) {
            throw new Error(
                `Ошибка создания договора: ${response.status} ${response.statusText}`
            )
        }

        return await response.json()
    }

    const handleAddContract = async () => {
        if (!yearPeriod || isCreatingContract) return

        try {
            setIsCreatingContract(true)

            const newContract = await createNewDraftEpContract(yearPeriod)

            if (!newContract?.contract_reg_number) {
                throw new Error(
                    'Не удалось получить реестровый номер нового договора'
                )
            }

            navigate(
                `/hub/contract/${newContract.contract_reg_number}/contracts/draft/${newContract.guid}`,
                {
                    state: {
                        from: 'registry',
                        fromStage: new URLSearchParams(location.search).get(
                            'stage'
                        ),
                        fromYear: yearPeriod,
                    },
                }
            )
        } catch (error) {
            console.error('Ошибка при создании нового договора:', error)
            setUploadMessage('Ошибка при создании нового договора.')
        } finally {
            setIsCreatingContract(false)
        }
    }

    // Обновление договоров из ЕИС --- НАЧАЛО --------------------------------------------------------

    const handleDeleteUpdateItem = (itemRegNumber: string) => {
        setUpdateItems((prevItems) =>
            prevItems.filter(
                (item) => item.contract_reg_number !== itemRegNumber
            )
        )
        setSelectedItems((prevSelectedItems) =>
            prevSelectedItems.filter(
                (selectedRegNumber) => selectedRegNumber !== itemRegNumber
            )
        )
    }

    const handleUpdateContractPopup = () => {
        if (updateContractPopup == false) {
            const filteredData = items.filter((item) =>
                selectedItems.includes(item.contract_reg_number)
            )
            setUpdateItems(filteredData)
        }
        setUpdateContractPopup(!updateContractPopup)
        setRowLoadStatus({})
        setRowLoadError({})
    }

    const updatePerformanceFromEis = async (
        guid: string,
        signal?: AbortSignal
    ): Promise<{
        ok: boolean
        contract?: Contract
        error?: string
        return_to_ready?: boolean
    }> => {
        setLoading(true)

        const url =
            process.env.REACT_APP_API_URL +
            `/api/backend/v1/contract_from_eis?contract_guid=${guid}`

        try {
            const response = await fetch(url, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                signal,
            })

            // --- обработка ошибок ---
            let errorData: any = null
            try {
                errorData = await response.json()
            } catch (e) {
                console.error('Failed to parse JSON from response:', e)
            }

            const detailMsg: string =
                (errorData && (errorData.detail || errorData.message)) ||
                `Unexpected error. Status: ${response.status}`

            const change_status: boolean = detailMsg.includes(
                'Подготовка к размещению'
            )
                ? true
                : false

            // Если не ОК по HTTP, но и без message — тоже как ошибка
            if (!response.ok) {
                setLoading(false)
                return {
                    ok: false,
                    error: detailMsg,
                    return_to_ready: change_status,
                }
            }

            // Успех — вернули само исполнение
            setLoading(false)
            return {
                ok: true,
                error: `Обновлено`,
            }
        } catch (e: any) {
            // Закрыть — это не ошибка бизнеса, вернем понятное сообщение
            if (e?.name === 'AbortError') {
                setLoading(false)
                return { ok: false, error: 'Операция отменена' }
            }
            setLoading(false)
            return { ok: false, error: String(e) }
        } finally {
            setLoading(false)
        }
    }

    const handleUpdateContractClick = async () => {
        if (!updateItems?.length) return

        setLoading(true)
        const controller = new AbortController()
        abortImportRef.current = controller

        try {
            // Последовательно. При желании можно сделать параллель с семафором.
            for (const row of updateItems) {
                if (controller.signal.aborted) break

                const key = String(row.guid)
                setRowLoadStatus((prev) => ({ ...prev, [key]: 'pending' }))
                setRowLoadError((prev) => ({ ...prev, [key]: undefined }))

                const res = await updatePerformanceFromEis(
                    key,
                    controller.signal
                )

                if (res.ok) {
                    setRowLoadStatus((prev) => ({
                        ...prev,
                        [key]: 'success',
                    }))
                } else if (res.return_to_ready == true) {
                    setRowLoadStatus((prev) => ({
                        ...prev,
                        [key]: 'error_back',
                    }))
                    setRowLoadError((prev) => ({ ...prev, [key]: res.error }))
                } else {
                    setRowLoadStatus((prev) => ({ ...prev, [key]: 'error' }))
                    setRowLoadError((prev) => ({ ...prev, [key]: res.error }))
                }
            }
        } finally {
            setLoading(false)
            abortImportRef.current = null
            loadContracts(1, filter)
            loadContractCountStage()
        }
    }

    // Обновление исполнения из ЕИС --- КОНЕЦ --------------------------------------------------------

    return (
        <div className="h-full flex flex-col bg-[#f0f3f5] dark:bg-[#181818]">
            <div className="bg-[#e5e5e5] dark:bg-[#111111]">
                <ContractHeader
                    theme={theme}
                    yearPeriod={yearPeriod}
                    onYearPeriodChange={setYearPeriod}
                    contractCountAndSum={contractCountAndSum}
                    loadingCountSum={loadingCountSum}
                />
                <ContractStageTab
                    stage={stage}
                    edit={editStage}
                    ready={readyStage}
                    send={sendStage}
                    perf={perfStage}
                    close={closeStage}
                    all={allStage}
                    onTabChange={(newStage) => {
                        setStage(newStage)
                    }}
                    onTabClick={(selectAllFlag) => setSelectAll(selectAllFlag)}
                    theme={theme}
                />
            </div>

            {/* // body */}
            <div className="plan-positions flex p-5 h-[calc(100vh_-_96px_-_28px)]">
                {/* table */}
                <div
                    id="printable-content"
                    className="flex flex-col w-full rounded bg-[#e4e6e8] dark:bg-[#1D1C1E]"
                >
                    {/* table toolbar */}
                    <div className="flex flex-row gap-2 p-2 h-10 rounded-t text-[#181818] dark:text-[#FFFFFF] bg-[#d2d5d8] dark:bg-[#333033]">
                        <HoverTooltip tooltipText="Выделить все">
                            <div className="w-6 h-6 grid rounded-full place-items-center hover:bg-[#FFFFFF] dark:hover:bg-[#656365]">
                                <input
                                    type="checkbox"
                                    className="p-2"
                                    checked={selectAll}
                                    onChange={handleSelectAllChange}
                                />
                            </div>
                        </HoverTooltip>

                        <TableStageToolbarButtons
                            role={isUserRoleName}
                            stage={stage}
                            isSelect={selectedItems.length > 0}
                            isImportingFromEis={isImportingFromEis}
                            multiSelect={selectedItems.length == 1}
                            filter={filter}
                            theme={theme}
                            actions={{
                                add_new: handleAddContract,
                                import: handleImportFromEisPopup,
                                update: handleUpdateContractPopup,
                                delete: handleDeleteContractPopup,
                                filter: handleFilter,
                            }}
                            // status = {}
                            // multiSelect = {selectedItems.length > 0}
                        />
                    </div>

                    {/* table header (name) */}

                    <TableHeader
                        stage={stage}
                        hasScroll={hasScroll}
                        filter={filter}
                        theme={theme}
                        onFilterErase={() => {
                            handleFilterErase()
                        }}
                        onAcceptFilter={() => {
                            handleFilterChange()
                        }}
                        // Фильтрация для "Номер договора"
                        onChangeFilterSettingCriteriaContractName={(value) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        contract_name: {
                                            ...filter.filter_settings.criteria
                                                ?.contract_name,
                                            query: value,
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)

                            if (value == null) {
                                handleFilterChange(newFilter)
                            }
                        }}
                        onChangeFilterSettingCriteriaContractNameSort={(
                            value
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        contract_name: {
                                            ...filter.filter_settings.criteria
                                                ?.contract_name,
                                            sort_order: value, // Обновляем только сортировку
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)

                            if (value == null) {
                                handleFilterChange(newFilter)
                            }
                        }}
                        // Фильтрация для "Статуса договора"
                        onChangeFilterSettingCriteriaContractStatus={(
                            values
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        contract_status: {
                                            ...filter.filter_settings.criteria
                                                ?.contract_status,
                                            values:
                                                values === null ? null : values, // если values равно null, передаем undefined
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)

                            if (values == null) {
                                handleFilterChange(newFilter)
                            }
                        }}
                        onChangeFilterSettingCriteriaContractStatusSort={(
                            value
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        contract_status: {
                                            ...filter.filter_settings.criteria
                                                ?.contract_status,
                                            sort_order: value, // Обновляем только сортировку
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)

                            if (value == null) {
                                handleFilterChange(newFilter)
                            }
                        }}
                        // Фильтрация для "Предмета договора"
                        onChangeFilterSettingCriteriaSubjectContract={(
                            value
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        subject_contract: {
                                            ...filter.filter_settings.criteria
                                                ?.subject_contract,
                                            query: value,
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)

                            if (value == null) {
                                handleFilterChange(newFilter)
                            }
                        }}
                        onChangeFilterSettingCriteriaSubjectContractSort={(
                            value
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        subject_contract: {
                                            ...filter.filter_settings.criteria
                                                ?.subject_contract,
                                            sort_order: value, // Обновляем только сортировку
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)

                            if (value == null) {
                                handleFilterChange(newFilter)
                            }
                        }}
                        // Фильтрация для "Сумма договора"
                        onChangeFilterSettingCriteriaPrice={(
                            valueMin,
                            valueMax
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        price: {
                                            ...filter.filter_settings.criteria
                                                ?.price,
                                            min: valueMin,
                                            max: valueMax,
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)

                            if (valueMin == null || valueMax == null) {
                                handleFilterChange(newFilter)
                            }
                        }}
                        onChangeFilterSettingCriteriaPriceSort={(value) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        price: {
                                            ...filter.filter_settings.criteria
                                                ?.price,
                                            sort_order: value, // Обновляем только сортировку
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)

                            if (value == null) {
                                handleFilterChange(newFilter)
                            }
                        }}
                        // Фильтрация для "Дата договора"
                        onChangeFilterSettingCriteriaContractDate={(
                            valueFrom,
                            valueTo
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        contract_date: {
                                            ...filter.filter_settings.criteria
                                                ?.contract_date,
                                            from: valueFrom,
                                            to: valueTo,
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)

                            if (valueFrom == null || valueTo == null) {
                                handleFilterChange(newFilter)
                            }
                        }}
                        onChangeFilterSettingCriteriaContractDateSort={(
                            value
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        contract_date: {
                                            ...filter.filter_settings.criteria
                                                ?.contract_date,
                                            sort_order: value, // Обновляем только сортировку
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)

                            if (value == null) {
                                handleFilterChange(newFilter)
                            }
                        }}
                        // Фильтрация для "Срок исполнения"
                        onChangeFilterSettingCriteriaContractEndExecutionDate={(
                            valueFrom,
                            valueTo
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        end_execution_date: {
                                            ...filter.filter_settings.criteria
                                                ?.end_execution_date,
                                            from: valueFrom,
                                            to: valueTo,
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)

                            if (valueFrom == null || valueTo == null) {
                                handleFilterChange(newFilter)
                            }
                        }}
                        onChangeFilterSettingCriteriaContractEndExecutionDateSort={(
                            value
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        end_execution_date: {
                                            ...filter.filter_settings.criteria
                                                ?.end_execution_date,
                                            sort_order: value, // Обновляем только сортировку
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)

                            if (value == null) {
                                handleFilterChange(newFilter)
                            }
                        }}
                        // Фильтрация для "Тип объекта закупки"
                        onChangeFilterSettingCriteriaTypeObjectContract={(
                            values
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        type_object_contract: {
                                            ...filter.filter_settings.criteria
                                                ?.type_object_contract,
                                            values:
                                                values === null ? null : values, // если values равно null, передаем undefined
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)

                            if (values == null) {
                                handleFilterChange(newFilter)
                            }
                        }}
                        onChangeFilterSettingCriteriaTypeObjectContractSort={(
                            value
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        type_object_contract: {
                                            ...filter.filter_settings.criteria
                                                ?.type_object_contract,
                                            sort_order: value, // Обновляем только сортировку
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)

                            if (value == null) {
                                handleFilterChange(newFilter)
                            }
                        }}
                        // Фильтрация для "Подразделение"
                        onChangeFilterSettingCriteriaStructGuid={(values) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        struct_guid: {
                                            ...filter.filter_settings.criteria
                                                ?.struct_guid,
                                            values:
                                                values === null ? null : values, // если values равно null, передаем undefined
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)

                            if (values == null) {
                                handleFilterChange(newFilter)
                            }
                        }}
                        onChangeFilterSettingCriteriaStructGuidSort={(
                            value
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        struct_guid: {
                                            ...filter.filter_settings.criteria
                                                ?.struct_guid,
                                            sort_order: value, // Обновляем только сортировку
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)

                            if (value == null) {
                                handleFilterChange(newFilter)
                            }
                        }}
                        // Фильтрация для "Реестровый номер договора"
                        onChangeFilterSettingCriteriaContractRegNumber={(
                            value
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        contract_reg_number: {
                                            ...filter.filter_settings.criteria
                                                ?.contract_reg_number,
                                            query: value,
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)

                            if (value == null) {
                                handleFilterChange(newFilter)
                            }
                        }}
                        onChangeFilterSettingCriteriaContractRegNumberSort={(
                            value
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        contract_reg_number: {
                                            ...filter.filter_settings.criteria
                                                ?.contract_reg_number,
                                            sort_order: value, // Обновляем только сортировку
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)

                            if (value == null) {
                                handleFilterChange(newFilter)
                            }
                        }}
                        // Фильтрация для "Дата изменения"
                        onChangeFilterSettingCriteriaCreatedDate={(
                            valueFrom,
                            valueTo
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        sys_updated_at: {
                                            ...filter.filter_settings.criteria
                                                ?.sys_updated_at,
                                            from: valueFrom,
                                            to: valueTo,
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)

                            if (valueFrom == null || valueTo == null) {
                                handleFilterChange(newFilter)
                            }
                        }}
                        onChangeFilterSettingCriteriaCreatedDateSort={(
                            value
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        sys_updated_at: {
                                            ...filter.filter_settings.criteria
                                                ?.sys_updated_at,
                                            sort_order: value, // Обновляем только сортировку
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)

                            if (value == null) {
                                handleFilterChange(newFilter)
                            }
                        }}
                        // Фильтрация для "Пользователь"
                        onChangeFilterSettingCriteriaSupervisorUserGuid={(
                            values
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        supervisor_user_guid: {
                                            ...filter.filter_settings.criteria
                                                ?.supervisor_user_guid,
                                            values:
                                                values === null ? null : values, // если values равно null, передаем undefined
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)

                            if (values == null) {
                                handleFilterChange(newFilter)
                            }
                        }}
                        onChangeFilterSettingCriteriaSupervisorUserGuidSort={(
                            value
                        ) => {
                            if (!filter) return // Если фильтра ещё нет, ничего не делаем
                            const newFilter: UserFilter = {
                                ...filter,
                                filter_settings: {
                                    ...filter.filter_settings,
                                    criteria: {
                                        ...filter.filter_settings.criteria,
                                        supervisor_user_guid: {
                                            ...filter.filter_settings.criteria
                                                ?.supervisor_user_guid,
                                            sort_order: value, // Обновляем только сортировку
                                        },
                                    },
                                },
                            }
                            setFilter(newFilter)

                            if (value == null) {
                                handleFilterChange(newFilter)
                            }
                        }}
                    />

                    {/* table data */}
                    <div
                        ref={scrollRef}
                        className="flex flex-col gap-1 my-1 h-dvh overflow-y-auto custom-scrollbar"
                        onScroll={(e) => {
                            const { scrollTop, scrollHeight, clientHeight } =
                                e.currentTarget

                            if (
                                page !== -1 && // ещё есть данные
                                !isLoadingContracts &&
                                scrollHeight - scrollTop - clientHeight < 100
                            ) {
                                if (filter?.filter_settings?.filter_enabled) {
                                    loadContracts(page, filter)
                                } else {
                                    loadContracts(page)
                                }
                            }
                        }}
                        onContextMenu={handleContextMenu} // Обрабатываем правый клик
                    >
                        {/* Вывод позиций */}
                        <>
                            {isLoadingContracts && page === 1 ? (
                                <div className="flex w-full h-full items-center justify-center">
                                    <LoadingIcon />
                                </div>
                            ) : !items.length ? (
                                <div className="flex w-full h-12 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A] text-[11px]">
                                    Нет данных
                                </div>
                            ) : (
                                items.map((item, index) => (
                                    <ContractsTableRow
                                        key={index}
                                        stage={stage}
                                        keyindex={index + 1}
                                        contract={item}
                                        structData={structureList}
                                        selected={selectedItems.includes(
                                            item.contract_reg_number
                                        )}
                                        onClick={(contract_reg_number) => {
                                            handleCheckboxChange(
                                                contract_reg_number
                                            )
                                        }}
                                        onDoubleClick={(
                                            contract_reg_number
                                        ) => {
                                            navigate(
                                                `/hub/contract/${contract_reg_number}?tab=Карточка договора`,
                                                {
                                                    state: {
                                                        fromStage:
                                                            new URLSearchParams(
                                                                location.search
                                                            ).get('stage'),
                                                        fromYear: yearPeriod,
                                                    },
                                                }
                                            )
                                        }}
                                        onRightClick={(contract_reg_number) => {
                                            handleRightClickCheckboxChange(
                                                contract_reg_number
                                            )
                                        }}
                                        theme={theme}
                                        currency={currencyList}
                                    />
                                ))
                            )}
                            {isLoadingContracts &&
                                page > 1 &&
                                items.length > 0 && (
                                    <div className="flex w-full items-center justify-center">
                                        <LoadingIcon />
                                    </div>
                                )}
                        </>
                    </div>

                    {/* table buttons */}
                    <div className="flex flex-row gap-3 p-2 px-[22px] rounded-b h-10 text-[#181818] dark:text-[#FFFFFF] bg-[#d2d5d8] dark:bg-[#333033]">
                        <TableStageToolbarDownButtons
                            role={isUserRoleName}
                            stage={stage}
                            isSelect={selectedItems.length > 0}
                            isImportingFromEis={isImportingFromEis}
                            multiSelect={selectedItems.length == 1}
                            theme={theme}
                            actions={{
                                add_new: handleAddContract,
                                import: handleImportFromEisPopup,
                                update: handleUpdateContractPopup,
                                delete: handleDeleteContractPopup,
                            }}
                            // status = {}
                            // multiSelect = {selectedItems.length > 0}
                        />
                    </div>
                </div>
            </div>
            {updateContractPopup && (
                <div className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center">
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-[1000px] max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-row w-full items-center justify-stsrt gap-2">
                            <svg
                                className="w-5 h-5"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                id="times-circle"
                            >
                                <path
                                    fill="#FFBF00"
                                    d="M12,14a1.25,1.25,0,1,0,1.25,1.25A1.25,1.25,0,0,0,12,14Zm0-1.5a1,1,0,0,0,1-1v-3a1,1,0,0,0-2,0v3A1,1,0,0,0,12,12.5ZM12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Z"
                                ></path>
                            </svg>
                            <h2 className="text-[#181818] dark:text-[#FFFFFF] text-[18px]">
                                Обновление данных
                            </h2>
                        </div>
                        <div className="flex flex-col mt-2 space-y-1">
                            <label className="text-[14px] text-[#181818] dark:text-[#FFFFFF]">
                                Вы уверены, что хотите начать обновление?
                            </label>
                            <div className="flex flex-row w-full space-x-5 pt-2">
                                <div className="flex flex-col w-full">
                                    {/* name */}
                                    <div className="flex flex-row gap-2 px-1 py-2 rounded-t-lg text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                                        <div className="flex w-10 items-center justify-center text-center">
                                            №
                                        </div>
                                        <div className="flex w-20 items-center justify-center text-center">
                                            Номер договора
                                        </div>
                                        <div className="flex flex-1 items-center justify-center text-center">
                                            Предмет договора
                                        </div>
                                        <div className="flex w-40 items-center justify-center text-center">
                                            Контрагент
                                        </div>
                                        <div className="flex w-20 items-center justify-center text-center">
                                            Дата договора
                                        </div>
                                        <div className="flex w-20 items-center justify-center text-center">
                                            Сумма договора
                                        </div>
                                        <div className="flex w-6 items-center justify-center"></div>
                                        <div className="flex w-6 items-center justify-center"></div>
                                    </div>
                                    {/* data */}
                                    <div className="flex flex-col h-[300px] gap-2 py-1 px-1 grow bg-[#e4e6e8] dark:bg-[#1D1C1E] rounded-b-lg overflow-y-auto custom-scrollbar">
                                        <div className="flex flex-col w-full space-y-1">
                                            {updateItems.map((item, index) => (
                                                <div
                                                    key={item.guid}
                                                    className="flex flex-row py-1 rounded text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] hover:bg-[#FFFFFF] dark:bg-[#2A282B] dark:hover:bg-[#373538]"
                                                >
                                                    <div className="flex flex-row w-full">
                                                        <div className="flex flex-row flex-1 min-h-8 gap-2 text-[#181818] dark:text-[#FFFFFF] text-[11px]">
                                                            <div className="flex w-10 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                                                                {index + 1}
                                                            </div>
                                                            <div className="flex w-20 items-center justify-center">
                                                                <span className="fourrow">
                                                                    {item.contract_name
                                                                        ? item.contract_name
                                                                        : '-'}
                                                                </span>
                                                            </div>
                                                            <div className="flex flex-1 items-center justify-left p-1">
                                                                {item.subject_contract ? (
                                                                    <span className="fourrow">
                                                                        {
                                                                            item.subject_contract
                                                                        }
                                                                    </span>
                                                                ) : (
                                                                    '-'
                                                                )}
                                                            </div>
                                                            <div className="flex w-40 items-center justify-center text-center">
                                                                <span className="fourrow">
                                                                    {item
                                                                        .suppliers[0]
                                                                        .supplier_short_name
                                                                        ? item
                                                                              .suppliers[0]
                                                                              .supplier_short_name
                                                                        : '-'}
                                                                </span>
                                                            </div>
                                                            <div className="flex w-20 items-center justify-center text-center">
                                                                {item.contract_date
                                                                    ?.split('-')
                                                                    .reverse()
                                                                    .join('-')
                                                                    .replace(
                                                                        /[\-]/g,
                                                                        '.'
                                                                    )}
                                                            </div>
                                                            <div className="flex w-20 items-center justify-end">
                                                                {Number(
                                                                    item.price
                                                                ).toLocaleString(
                                                                    'ru-RU',
                                                                    {
                                                                        minimumFractionDigits: 2,
                                                                    }
                                                                )}
                                                            </div>
                                                            <div className="flex w-6 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A] ">
                                                                <button
                                                                    onClick={() =>
                                                                        handleDeleteUpdateItem(
                                                                            item.contract_reg_number
                                                                        )
                                                                    }
                                                                    className="flex w-6 h-6 rounded-full items-center justify-center hover:bg-[#ed9cae] dark:hover:bg-[#701F1F]"
                                                                >
                                                                    <svg
                                                                        className="w-3 h-3"
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        viewBox="0 0 24 24"
                                                                        id="trash-alt"
                                                                    >
                                                                        <path
                                                                            fill={
                                                                                theme ==
                                                                                'dark'
                                                                                    ? '#FFFFFF'
                                                                                    : '#181818'
                                                                            }
                                                                            d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                                                        ></path>
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                            <div className="flex w-6 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A] ">
                                                                {(() => {
                                                                    const key =
                                                                        String(
                                                                            item.guid
                                                                        )
                                                                    const st =
                                                                        rowLoadStatus[
                                                                            key
                                                                        ]

                                                                    if (
                                                                        st ===
                                                                        'pending'
                                                                    ) {
                                                                        return (
                                                                            <svg
                                                                                className="animate-spin h-4 w-4"
                                                                                xmlns="http://www.w3.org/2000/svg"
                                                                                fill="none"
                                                                                viewBox="0 0 24 24"
                                                                            >
                                                                                <circle
                                                                                    className="opacity-25"
                                                                                    cx="12"
                                                                                    cy="12"
                                                                                    r="10"
                                                                                    stroke="currentColor"
                                                                                    strokeWidth="4"
                                                                                ></circle>
                                                                                <path
                                                                                    className="opacity-75"
                                                                                    fill="currentColor"
                                                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                                                ></path>
                                                                            </svg>
                                                                        )
                                                                    }

                                                                    if (
                                                                        st ===
                                                                        'success'
                                                                    ) {
                                                                        return (
                                                                            <svg
                                                                                xmlns="http://www.w3.org/2000/svg"
                                                                                className="h-4 w-4 text-green-600 dark:text-green-400"
                                                                                viewBox="0 0 24 24"
                                                                                fill="currentColor"
                                                                                aria-label="Импортирован"
                                                                            >
                                                                                <path
                                                                                    fillRule="evenodd"
                                                                                    d="M18.71,7.21a1,1,0,0,0-1.42,0L9.84,14.67,6.71,11.53A1,1,0,1,0,5.29,13l3.84,3.84a1,1,0,0,0,1.42,0l8.16-8.16A1,1,0,0,0,18.71,7.21Z"
                                                                                    clipRule="evenodd"
                                                                                />
                                                                            </svg>
                                                                        )
                                                                    }

                                                                    if (
                                                                        st ===
                                                                        'error_back'
                                                                    ) {
                                                                        return (
                                                                            <HoverTooltip
                                                                                tooltipText={
                                                                                    rowLoadError[
                                                                                        key
                                                                                    ] ||
                                                                                    'Ошибка'
                                                                                }
                                                                                align="left"
                                                                            >
                                                                                <div>
                                                                                    <svg
                                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                                        className="h-4 w-4"
                                                                                        viewBox="0 0 24 24"
                                                                                        fill="currentColor"
                                                                                        aria-label="Ошибка"
                                                                                    >
                                                                                        <path
                                                                                            fill="#FFBF00"
                                                                                            d="M12,14a1.25,1.25,0,1,0,1.25,1.25A1.25,1.25,0,0,0,12,14Zm0-1.5a1,1,0,0,0,1-1v-3a1,1,0,0,0-2,0v3A1,1,0,0,0,12,12.5ZM12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Z"
                                                                                        />
                                                                                    </svg>
                                                                                </div>
                                                                            </HoverTooltip>
                                                                        )
                                                                    }

                                                                    if (
                                                                        st ===
                                                                        'error'
                                                                    ) {
                                                                        return (
                                                                            <HoverTooltip
                                                                                tooltipText={
                                                                                    rowLoadError[
                                                                                        key
                                                                                    ] ||
                                                                                    'Ошибка'
                                                                                }
                                                                                align="left"
                                                                            >
                                                                                <div>
                                                                                    <svg
                                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                                        className="h-4 w-4 text-red-600 dark:text-red-400"
                                                                                        viewBox="0 0 24 24"
                                                                                        fill="currentColor"
                                                                                        aria-label="Ошибка"
                                                                                    >
                                                                                        <path
                                                                                            fillRule="evenodd"
                                                                                            d="M13.41,12l4.3-4.29a1,1,0,1,0-1.42-1.42L12,10.59,7.71,6.29A1,1,0,0,0,6.29,7.71L10.59,12l-4.3,4.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l4.29,4.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z"
                                                                                            clipRule="evenodd"
                                                                                        />
                                                                                    </svg>
                                                                                </div>
                                                                            </HoverTooltip>
                                                                        )
                                                                    }

                                                                    // idle
                                                                    return null
                                                                })()}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-row mt-5 text-right justify-end">
                            {isLoading ? (
                                ''
                            ) : (
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={() => handleUpdateContractPopup()}
                                >
                                    Закрыть
                                </button>
                            )}
                            <button
                                className={`flex px-4 py-1 min-w-[120px] h-6 ml-2 justify-center items-center rounded-[10px] shadow-md shadow-[#18181830] text-xs text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]`}
                                onClick={handleUpdateContractClick} // Вызвать handleStageChange
                                disabled={isLoading ? true : false}
                            >
                                {isLoading ? (
                                    <svg
                                        className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        ></path>
                                    </svg>
                                ) : (
                                    'Обновить'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {importFromEisPopup && (
                <div className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center">
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-[700px] max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-row gap-2 items-center justify-left">
                            <h2 className="text-[#181818] dark:text-[#FFFFFF]">
                                Импрот договоров из ЕИС
                            </h2>
                        </div>
                        <div className="flex flex-row w-full gap-3 items-start justify-start pt-2">
                            <Checkbox
                                label="Массовый импорт договоров"
                                checked={massImportMode || false}
                                onChange={() => {
                                    setMassImportMode(!massImportMode)
                                }}
                                disabled={false}
                            />
                        </div>

                        {massImportMode ? (
                            <>
                                <div className="flex flex-row w-full gap-3 items-end justify-end pt-2">
                                    <div className="flex flex-col w-full gap-1 items-start justify-start">
                                        <div className="flex flex-col w-full items-start justify-start text-[12.8px] text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            Начало периода
                                        </div>
                                        <div className="flex w-full items-start justify-start">
                                            <input
                                                type="date"
                                                style={{
                                                    colorScheme:
                                                        theme == 'dark'
                                                            ? 'dark'
                                                            : 'light',
                                                }}
                                                className="form-input w-full bg-[#1F1E1F]"
                                                value={searchFromDate}
                                                onChange={(e) =>
                                                    setSearchFromDate(
                                                        e.target.value
                                                    )
                                                }
                                            ></input>
                                        </div>
                                    </div>
                                    <div className="flex flex-col w-full gap-1 items-start justify-start">
                                        <div className="flex flex-col w-full items-start justify-start text-[12.8px] text-[#6A6A6A] dark:text-[#8A8A8A] ">
                                            Конец периода
                                        </div>
                                        <div className="flex w-full items-start justify-start">
                                            <input
                                                type="date"
                                                style={{
                                                    colorScheme:
                                                        theme == 'dark'
                                                            ? 'dark'
                                                            : 'light',
                                                }}
                                                className="form-input w-full bg-[#1F1E1F]"
                                                value={searchToDate}
                                                onChange={(e) =>
                                                    setSearchToDate(
                                                        e.target.value
                                                    )
                                                }
                                            ></input>
                                        </div>
                                    </div>
                                    <button
                                        className={`flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] text-xs ml-2 items-center justify-center
                                        ${
                                            searchFromDate && searchToDate
                                                ? !isSearchingContractFromEis
                                                    ? 'shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]'
                                                    : 'dark:text-[#8A8A8A] text-[#6a6a6a] bg-[#ededed] dark:bg-[#494849]'
                                                : 'dark:text-[#8A8A8A] text-[#6a6a6a] bg-[#ededed] dark:bg-[#494849]'
                                        }
                                        `}
                                        disabled={
                                            searchFromDate && searchToDate
                                                ? isSearchingContractFromEis
                                                    ? true
                                                    : false
                                                : true
                                        }
                                        onClick={() => {
                                            const controller =
                                                new AbortController()
                                            importPopupAbortRef.current =
                                                controller

                                            searchContractFromEis(
                                                searchFromDate,
                                                searchToDate,
                                                controller.signal
                                            )
                                        }}
                                    >
                                        {isSearchingContractFromEis ? (
                                            <svg
                                                className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                ></circle>
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                ></path>
                                            </svg>
                                        ) : (
                                            'Поиск'
                                        )}
                                    </button>
                                </div>
                                <div className="flex flex-col w-full pt-2">
                                    {/* name */}
                                    <div className="flex flex-row gap-1 px-1 py-2 rounded-t-lg text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                                        <div className="flex w-10 items-center justify-center text-center">
                                            №
                                        </div>
                                        <div className="flex w-40 items-center justify-center text-center">
                                            Регистрационный номер
                                        </div>
                                        <div className="flex flex-1 w-20 items-center justify-center text-center">
                                            Номер договора
                                        </div>
                                        <div className="flex w-28 items-center justify-center text-center">
                                            Сумма
                                        </div>
                                        <div className="flex w-28 items-center justify-center text-center">
                                            Дата договора
                                        </div>
                                        <div className="flex w-7 items-center justify-center"></div>
                                    </div>
                                    {/* data */}
                                    <div className="flex flex-col h-[300px] gap-1 py-1 px-1 grow rounded-b-lg overflow-y-auto custom-scrollbar bg-[#e4e6e8] dark:bg-[#1D1C1E]">
                                        <div className="flex flex-col w-full space-y-1">
                                            {isSearchingContractFromEis ? (
                                                <div className="flex flex-row rounded text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] hover:bg-[#FFFFFF] dark:bg-[#2A282B] dark:hover:bg-[#373538]">
                                                    <div className="flex flex-row w-full text-[11px] min-h-8 gap-1 items-center justify-center">
                                                        <svg
                                                            className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <circle
                                                                className="opacity-25"
                                                                cx="12"
                                                                cy="12"
                                                                r="10"
                                                                stroke="currentColor"
                                                                strokeWidth="4"
                                                            ></circle>
                                                            <path
                                                                className="opacity-75"
                                                                fill="currentColor"
                                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                            ></path>
                                                        </svg>
                                                        <span>Поиск...</span>
                                                    </div>
                                                </div>
                                            ) : serachContractFromEisData?.contract_count !=
                                              0 ? (
                                                serachContractFromEisData?.data.map(
                                                    (item, index) => (
                                                        <div
                                                            key={index}
                                                            className="flex flex-row rounded text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] hover:bg-[#FFFFFF] dark:bg-[#2A282B] dark:hover:bg-[#373538]"
                                                        >
                                                            <div className="flex flex-row w-full">
                                                                <div className="flex flex-row min-h-8 flex-1 gap-1 text-[#181818] dark:text-[#FFFFFF] text-[11px]">
                                                                    <div className="flex w-10 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                                                                        {index +
                                                                            1}
                                                                    </div>
                                                                    <div className="flex w-40 items-center justify-center">
                                                                        {
                                                                            item.contract_reg_number
                                                                        }
                                                                    </div>
                                                                    <div className="flex flex-1 w-20 items-center justify-center">
                                                                        {
                                                                            item.contract_number
                                                                        }
                                                                    </div>
                                                                    <div className="flex w-28 items-center justify-end">
                                                                        {item.price_currency ==
                                                                        '₽'
                                                                            ? Number(
                                                                                  item.price_amount
                                                                              ).toLocaleString(
                                                                                  'ru-RU',
                                                                                  {
                                                                                      minimumFractionDigits: 2,
                                                                                  }
                                                                              )
                                                                            : Number(
                                                                                  item.price_amount
                                                                              ).toLocaleString(
                                                                                  'ru-RU',
                                                                                  {
                                                                                      minimumFractionDigits: 2,
                                                                                  }
                                                                              ) +
                                                                              ' ' +
                                                                              item.price_currency}
                                                                    </div>
                                                                    <div className="flex w-28 items-center justify-center">
                                                                        {item.conclusion_date
                                                                            ?.split(
                                                                                '-'
                                                                            )
                                                                            .reverse()
                                                                            .join(
                                                                                '-'
                                                                            )
                                                                            .replace(
                                                                                /[\-]/g,
                                                                                '.'
                                                                            )}
                                                                    </div>
                                                                    <div className="flex w-7 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A] ">
                                                                        {(() => {
                                                                            const key =
                                                                                String(
                                                                                    item.contract_reg_number
                                                                                )
                                                                            const st =
                                                                                rowLoadStatus[
                                                                                    key
                                                                                ]

                                                                            if (
                                                                                st ===
                                                                                'pending'
                                                                            ) {
                                                                                return (
                                                                                    <svg
                                                                                        className="animate-spin h-4 w-4"
                                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                                        fill="none"
                                                                                        viewBox="0 0 24 24"
                                                                                    >
                                                                                        <circle
                                                                                            className="opacity-25"
                                                                                            cx="12"
                                                                                            cy="12"
                                                                                            r="10"
                                                                                            stroke="currentColor"
                                                                                            strokeWidth="4"
                                                                                        ></circle>
                                                                                        <path
                                                                                            className="opacity-75"
                                                                                            fill="currentColor"
                                                                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                                                        ></path>
                                                                                    </svg>
                                                                                )
                                                                            }

                                                                            if (
                                                                                st ===
                                                                                'success'
                                                                            ) {
                                                                                return (
                                                                                    <svg
                                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                                        className="h-4 w-4 text-green-600 dark:text-green-400"
                                                                                        viewBox="0 0 20 20"
                                                                                        fill="currentColor"
                                                                                        aria-label="Импортирован"
                                                                                    >
                                                                                        <path
                                                                                            fillRule="evenodd"
                                                                                            d="M18.71,7.21a1,1,0,0,0-1.42,0L9.84,14.67,6.71,11.53A1,1,0,1,0,5.29,13l3.84,3.84a1,1,0,0,0,1.42,0l8.16-8.16A1,1,0,0,0,18.71,7.21Z"
                                                                                            clipRule="evenodd"
                                                                                        />
                                                                                    </svg>
                                                                                )
                                                                            }

                                                                            if (
                                                                                st ===
                                                                                'error'
                                                                            ) {
                                                                                return (
                                                                                    <HoverTooltip
                                                                                        tooltipText={
                                                                                            rowLoadError[
                                                                                                key
                                                                                            ] ||
                                                                                            'Ошибка'
                                                                                        }
                                                                                        align="left"
                                                                                    >
                                                                                        <div>
                                                                                            <svg
                                                                                                xmlns="http://www.w3.org/2000/svg"
                                                                                                className="h-4 w-4 text-red-600 dark:text-red-400"
                                                                                                viewBox="0 0 20 20"
                                                                                                fill="currentColor"
                                                                                                aria-label="Ошибка"
                                                                                            >
                                                                                                <path
                                                                                                    fillRule="evenodd"
                                                                                                    d="M13.41,12l4.3-4.29a1,1,0,1,0-1.42-1.42L12,10.59,7.71,6.29A1,1,0,0,0,6.29,7.71L10.59,12l-4.3,4.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l4.29,4.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z"
                                                                                                    clipRule="evenodd"
                                                                                                />
                                                                                            </svg>
                                                                                        </div>
                                                                                    </HoverTooltip>
                                                                                )
                                                                            }

                                                                            // idle
                                                                            return null
                                                                        })()}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )
                                                )
                                            ) : (
                                                <div className="flex flex-row rounded text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] hover:bg-[#FFFFFF] dark:bg-[#2A282B] dark:hover:bg-[#373538]">
                                                    <div className="flex flex-row w-full text-[11px] min-h-8 items-center justify-center">
                                                        Нет данных
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-row mt-5 text-right justify-end">
                                    <button
                                        className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                        onClick={handleImportFromEisPopup}
                                    >
                                        Закрыть
                                    </button>

                                    {!clickUpdateButton ? (
                                        <button
                                            className={`flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] text-xs  ml-2 items-center justify-center 
                                            ${
                                                serachContractFromEisData &&
                                                serachContractFromEisData?.data
                                                    .length > 0
                                                    ? 'shadow-md shadow-[#18181830] dark:text-[#FFFFFF] selection:text-[#181818] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]'
                                                    : 'dark:text-[#8A8A8A] text-[#6a6a6a] bg-[#ededed] dark:bg-[#494849]'
                                            } `}
                                            disabled={
                                                !(
                                                    serachContractFromEisData &&
                                                    serachContractFromEisData
                                                        ?.data.length > 0
                                                )
                                            }
                                            onClick={importAllFoundContracts}
                                        >
                                            {isImportingFromEis ? (
                                                <svg
                                                    className="animate-spin h-4 w-4 shadow-md shadow-[#18181830] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40] text-[#181818] dark:text-[#FFFFFF]"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <circle
                                                        className="opacity-25"
                                                        cx="12"
                                                        cy="12"
                                                        r="10"
                                                        stroke="currentColor"
                                                        strokeWidth="4"
                                                    ></circle>
                                                    <path
                                                        className="opacity-75"
                                                        fill="currentColor"
                                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                    ></path>
                                                </svg>
                                            ) : (
                                                'Добавить'
                                            )}
                                        </button>
                                    ) : (
                                        ''
                                    )}
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="flex flex-col w-full gap-3 items-center justify-center pt-2">
                                    <div className="flex flex-col w-full gap-1 items-start justify-start">
                                        <div className="flex flex-col w-full items-start justify-start text-[12.8px] text-[#6A6A6A] dark:text-[#8A8A8A]">
                                            Реестровый номер договора
                                        </div>
                                        <div className="flex flex-row w-full items-center justify-center">
                                            <div className="flex w-full items-start justify-start">
                                                <input
                                                    type="text"
                                                    style={{
                                                        colorScheme:
                                                            theme == 'dark'
                                                                ? 'dark'
                                                                : 'light',
                                                    }}
                                                    className="form-input w-full bg-[#1F1E1F]"
                                                    value={contractRegNumber}
                                                    onChange={(e) =>
                                                        setContractRegNumber(
                                                            e.target.value
                                                        )
                                                    }
                                                ></input>
                                            </div>
                                            <div className="flex w-6 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A] ">
                                                {(() => {
                                                    if (
                                                        isSingleContractImporting
                                                    ) {
                                                        return (
                                                            <svg
                                                                className="animate-spin h-4 w-4"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                fill="none"
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <circle
                                                                    className="opacity-25"
                                                                    cx="12"
                                                                    cy="12"
                                                                    r="10"
                                                                    stroke="currentColor"
                                                                    strokeWidth="4"
                                                                ></circle>
                                                                <path
                                                                    className="opacity-75"
                                                                    fill="currentColor"
                                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                                ></path>
                                                            </svg>
                                                        )
                                                    }

                                                    if (
                                                        singleContractImportStatus ===
                                                        true
                                                    ) {
                                                        return (
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                className="h-4 w-4 text-green-600 dark:text-green-400"
                                                                viewBox="0 0 20 20"
                                                                fill="currentColor"
                                                                aria-label="Импортирован"
                                                            >
                                                                <path
                                                                    fillRule="evenodd"
                                                                    d="M18.71,7.21a1,1,0,0,0-1.42,0L9.84,14.67,6.71,11.53A1,1,0,1,0,5.29,13l3.84,3.84a1,1,0,0,0,1.42,0l8.16-8.16A1,1,0,0,0,18.71,7.21Z"
                                                                    clipRule="evenodd"
                                                                />
                                                            </svg>
                                                        )
                                                    }

                                                    if (
                                                        singleContractImportStatus ===
                                                        false
                                                    ) {
                                                        return (
                                                            <div>
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    className="h-4 w-4 text-red-600 dark:text-red-400"
                                                                    viewBox="0 0 20 20"
                                                                    fill="currentColor"
                                                                    aria-label="Ошибка"
                                                                >
                                                                    <path
                                                                        fillRule="evenodd"
                                                                        d="M13.41,12l4.3-4.29a1,1,0,1,0-1.42-1.42L12,10.59,7.71,6.29A1,1,0,0,0,6.29,7.71L10.59,12l-4.3,4.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l4.29,4.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z"
                                                                        clipRule="evenodd"
                                                                    />
                                                                </svg>
                                                            </div>
                                                        )
                                                    }

                                                    // idle
                                                    return null
                                                })()}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex flex-row w-full mt-5 text-right items-end justify-end">
                                        <button
                                            className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                            onClick={handleImportFromEisPopup}
                                        >
                                            Закрыть
                                        </button>
                                        {!isSingleContractImporting ? (
                                            <button
                                                className={`flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] text-xs ml-2 items-center justify-center ${
                                                    contractRegNumber
                                                        ? 'shadow-md shadow-[#18181830] placeholder:text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]'
                                                        : 'dark:text-[#8A8A8A] text-[#6a6a6a] bg-[#ededed] dark:bg-[#494849]'
                                                } `}
                                                disabled={!contractRegNumber}
                                                onClick={() => {
                                                    const controller =
                                                        new AbortController()
                                                    importPopupAbortRef.current =
                                                        controller

                                                    importContractFromEis(
                                                        contractRegNumber,
                                                        controller.signal
                                                    )
                                                }}
                                            >
                                                Добавить
                                            </button>
                                        ) : (
                                            ''
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
            {/* Контекстное меню */}
            {/* {menuVisible && (
                <div
                    className="absolute flex flex-col w-[200px] shadow-lg rounded-lg p-1 z-10 text-[12.8px] items-center justify-start gap-1 border border-[#b8b8b8] dark:border-[#696769] bg-[#d9dce0] dark:bg-[#333033]"
                    style={{
                        top: menuPosition.y,
                        left: menuPosition.x,
                    }}
                >
                    <TableRightClickButton
                        role={isUserRoleName}
                        stage={stage}
                        isSelect={selectedItems.length > 0}
                        isImportingFromEis={isImportingFromEis}
                        multiSelect={selectedItems.length == 1}
                        theme={theme}
                        actions={{
                            add: () => navigate(`/hub/contract/new`),
                        }}
                        // status = {}
                        // multiSelect = {selectedItems.length > 0}
                    />
                </div>
            )} */}
            {uploadMessage && (
                <div
                    className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center"
                    onClick={() => setUploadMessage(null)}
                >
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-fit max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-col text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-center gap-5">
                            <div className="flex flex-row text-sm text-[#181818] dark:text-[#FFFFFF] items-center justify-center gap-2">
                                <div
                                    className={`flex w-5 h-5 rounded-full items-center justify-center text-[#181818] dark:text-[#FFFFFF] ${
                                        uploadMessage ==
                                        'Данные успешно импортированы.'
                                            ? 'bg-[#83d2b7] dark:bg-[#187A33]'
                                            : 'bg-[#dc742f]'
                                    }`}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        className="h-4 w-4"
                                    >
                                        <path
                                            fill={
                                                theme == 'dark'
                                                    ? '#FFFFFF'
                                                    : '#181818'
                                            }
                                            d={
                                                uploadMessage ==
                                                'Данные успешно импортированы.'
                                                    ? 'M18.71,7.21a1,1,0,0,0-1.42,0L9.84,14.67,6.71,11.53A1,1,0,1,0,5.29,13l3.84,3.84a1,1,0,0,0,1.42,0l8.16-8.16A1,1,0,0,0,18.71,7.21Z'
                                                    : 'M13.41,12l4.3-4.29a1,1,0,1,0-1.42-1.42L12,10.59,7.71,6.29A1,1,0,0,0,6.29,7.71L10.59,12l-4.3,4.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l4.29,4.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z'
                                            }
                                        ></path>
                                    </svg>
                                </div>
                                {uploadMessage}
                            </div>
                            <div className="flex w-full flex-row items-center justify-end">
                                <button
                                    className="flex gap-3 px-5 py-1 h-6 min-w-[120px] rounded-[10px] text-[12.8px] items-center justify-center shadow-md shadow-[#18181830] text-xs text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]"
                                    onClick={() => setUploadMessage(null)}
                                >
                                    Ок
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {deleteContractPopup && (
                <div
                    className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center"
                    // onClick={handleDeleteContractPopup}
                >
                    <div
                        className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-[700px] max-h-[600px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex flex-row w-full items-center justify-stsrt gap-2">
                            <svg
                                className="w-5 h-5"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                id="times-circle"
                            >
                                <path
                                    fill="#C15C5C"
                                    d="M15.71,8.29a1,1,0,0,0-1.42,0L12,10.59,9.71,8.29A1,1,0,0,0,8.29,9.71L10.59,12l-2.3,2.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l2.29,2.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42L13.41,12l2.3-2.29A1,1,0,0,0,15.71,8.29Zm3.36-3.36A10,10,0,1,0,4.93,19.07,10,10,0,1,0,19.07,4.93ZM17.66,17.66A8,8,0,1,1,20,12,7.95,7.95,0,0,1,17.66,17.66Z"
                                ></path>
                            </svg>
                            <h2 className="text-[#181818] dark:text-[#FFFFFF] text-[18px]">
                                Удаление договора
                            </h2>
                        </div>
                        <div className="flex flex-col mt-2 space-y-1">
                            <label className="text-[14px] text-[#181818] dark:text-[#FFFFFF]">
                                Вы уверены, что хотите удалить договор? Это
                                действие является необратимым. Продолжить?
                            </label>
                            <div className="flex flex-row w-full space-x-5 pt-2">
                                <div className="flex flex-col w-full">
                                    {/* name */}
                                    <div className="flex flex-row gap-1 px-1 py-2 rounded-t-lg text-[10px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                                        <div className="flex w-10 items-center justify-center text-center">
                                            №
                                        </div>
                                        <div className="flex w-20 items-center justify-center text-center">
                                            Номер договора
                                        </div>
                                        <div className="flex flex-1 items-center justify-center text-center">
                                            Предмет договора
                                        </div>
                                        <div className="flex w-28 items-center justify-center text-center">
                                            Сумма
                                        </div>
                                        <div className="flex w-6 items-center justify-center"></div>
                                    </div>
                                    {/* data */}
                                    <div className="flex flex-col h-[200px] gap-1 py-1 px-1 grow rounded-b-lg overflow-y-auto custom-scrollbar bg-[#e4e6e8] dark:bg-[#1D1C1E]">
                                        <div className="flex flex-col w-full space-y-1">
                                            {deleteItems.map((item, index) => (
                                                <div
                                                    key={item.guid}
                                                    className="flex flex-row rounded text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] hover:bg-[#FFFFFF] dark:bg-[#2A282B] dark:hover:bg-[#373538]"
                                                >
                                                    <div className="flex flex-row w-full">
                                                        <div className="flex flex-row min-h-8 flex-1 gap-1 text-[#181818] dark:text-[#FFFFFF] text-[11px]">
                                                            <div className="flex w-10 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                                                                {index + 1}
                                                            </div>
                                                            <div className="flex w-20 items-center justify-center">
                                                                {
                                                                    <span className="fourrow">
                                                                        item.contract_name
                                                                    </span>
                                                                }
                                                            </div>
                                                            <div className="flex  flex-1 items-center justify-left p-1">
                                                                {
                                                                    item.subject_contract
                                                                }
                                                            </div>
                                                            <div className="flex w-24 items-center justify-end">
                                                                <HoverTooltip
                                                                    tooltipText={
                                                                        item.currency_digital_code
                                                                            ? String(
                                                                                  currencyList.find(
                                                                                      (
                                                                                          c
                                                                                      ) =>
                                                                                          c.num_code ===
                                                                                          String(
                                                                                              item.currency_digital_code
                                                                                          )
                                                                                  )
                                                                                      ?.name
                                                                              )
                                                                            : '-'
                                                                    }
                                                                >
                                                                    <span>
                                                                        {Number(
                                                                            item.price
                                                                        ).toLocaleString(
                                                                            'ru-RU',
                                                                            {
                                                                                minimumFractionDigits: 2,
                                                                            }
                                                                        )}{' '}
                                                                        {item.currency_digital_code
                                                                            ? item.currency_digital_code ==
                                                                              643
                                                                                ? ''
                                                                                : currencyList.find(
                                                                                      (
                                                                                          c
                                                                                      ) =>
                                                                                          c.num_code ===
                                                                                          String(
                                                                                              item.currency_digital_code
                                                                                          )
                                                                                  )
                                                                                      ?.char_sign ||
                                                                                  '?'
                                                                            : ''}
                                                                    </span>
                                                                </HoverTooltip>
                                                            </div>
                                                            <div className="flex w-8 items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A] ">
                                                                <button
                                                                    onClick={() =>
                                                                        handleDeleteItem(
                                                                            item.guid
                                                                        )
                                                                    }
                                                                    className=" flex w-6 h-6 rounded-full items-center justify-center hover:bg-[#ed9cae] dark:hover:bg-[#701F1F]"
                                                                >
                                                                    <svg
                                                                        className="w-3 h-3"
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        viewBox="0 0 24 24"
                                                                        id="trash-alt"
                                                                    >
                                                                        <path
                                                                            fill={
                                                                                theme ==
                                                                                'dark'
                                                                                    ? '#FFFFFF'
                                                                                    : '#181818'
                                                                            }
                                                                            d="M10,18a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,10,18ZM20,6H16V5a3,3,0,0,0-3-3H11A3,3,0,0,0,8,5V6H4A1,1,0,0,0,4,8H5V19a3,3,0,0,0,3,3h8a3,3,0,0,0,3-3V8h1a1,1,0,0,0,0-2ZM10,5a1,1,0,0,1,1-1h2a1,1,0,0,1,1,1V6H10Zm7,14a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8H17Zm-3-1a1,1,0,0,0,1-1V11a1,1,0,0,0-2,0v6A1,1,0,0,0,14,18Z"
                                                                        ></path>
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-row mt-5 text-right justify-end gap-2">
                            <button
                                className={`flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] ml-2 items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] bg-[#ed9cae] hover:bg-[#ffa7ba] dark:bg-[#701F1F] dark:hover:bg-[#9e2c2c] dark:text-[#FFFFFF]`}
                                onClick={handleDeleteClick} // Вызвать handleDelete
                                disabled={isDeleting ? true : false}
                            >
                                {isDeleting ? (
                                    <svg
                                        className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        ></path>
                                    </svg>
                                ) : (
                                    'Удалить'
                                )}
                            </button>
                            {isDeleting ? (
                                ''
                            ) : (
                                <button
                                    className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                    onClick={handleDeleteContractPopup}
                                >
                                    Закрыть
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ContractListPage
